import React, { useEffect, useState, useRef } from 'react';
import { ResponsiveBar } from '@nivo/bar';
import _ from 'lodash';

// Function to parse datetime string to JavaScript Date
const parseCustomDateTime = (datetimeStr, topic) => {
  if (!datetimeStr || typeof datetimeStr !== 'string') {
    return null;
  }
  const parts = datetimeStr.split(' ');
  if (parts.length !== 2) return null;

  const [timePart, datePart] = parts;
  const timeComponents = timePart.split(':');
  const dateComponents = datePart.split('/');

  if (timeComponents.length !== 3 || dateComponents.length !== 3) {
    return null;
  }

  const [day, month, year] = dateComponents.map(Number);
  const [hours, minutes, seconds] = timeComponents.map(Number);

  // Assuming year 'YY' format, convert to 'YYYY'
  const fullYear = year < 100 ? 2000 + year : year;

  const date = new Date(fullYear, month - 1, day, hours, minutes, seconds);

  // 🇴🇲 Apply -1h30m offset only for "i_a6xle9" topic
  if (topic === "i_a6xle9") {
    date.setMinutes(date.getMinutes() - 90);
  }

  return date;
};


function BarChart({ data, graph_det }) {
  const [chartData, setChartData] = useState(null);
  const [dataValidationErrors, setDataValidationErrors] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);
  const hasInitializedRef = useRef(false); // Track if component has been initialized once

  // Initialize component immediately
  useEffect(() => {
    if (!hasInitializedRef.current) {
      setIsInitialized(true);
      hasInitializedRef.current = true;
    }
  }, []);

  // Function to update chart data
  const updateChartData = (inputData) => {
    if (!inputData || !Array.isArray(inputData)) {
      setDataValidationErrors(["Data must be an array"]);
      return;
    }

    const errors = [];
    const cleanData = inputData.map((d, index) => {
      const date = parseCustomDateTime(d.datetime, graph_det?.device_id?.topic);
      if (!date) {
        errors.push(`Invalid datetime format at index ${index}`);
        return null;
      }
      const value = parseFloat(d.value);
      if (isNaN(value)) {
        errors.push(`Invalid value at index ${index}`);
        return null;
      }
      return { ...d, datetime: date, value };
    }).filter(d => d !== null);

    setDataValidationErrors(errors);

    if (cleanData.length > 0) {
      let grped = _.groupBy(cleanData, d => {
        return d.datetime.toISOString();
      });

      let formattedData = _.map(grped, (value, key) => {
        return {
          x: key,
          y: _.sumBy(value, d => d.value)
        };
      });

      // Sort data by date in ascending order
      formattedData.sort((a, b) => new Date(a.x) - new Date(b.x));

      setChartData(formattedData);
    }
  };

  // Main effect for data updates - only runs when data changes
  useEffect(() => {
    if (data && isInitialized) {
      // Only show loading for the initial load
      if (!hasInitializedRef.current || !chartData) {
        setIsLoading(true);
        
        // Small delay only for initial load
        const timer = setTimeout(() => {
          updateChartData(data);
          setIsLoading(false);
        }, 500);
        
        return () => clearTimeout(timer);
      } else {
        // For subsequent updates, update immediately without loading state
        updateChartData(data);
      }
    }
  }, [data, isInitialized]);

  // Container style with immediate black background
  const containerStyle = {
    position: 'relative',
    height: '400px',
    width: '100%',
    backgroundColor: 'transparent',
    borderRadius: '4px',
    overflow: 'hidden'
  };

  // Don't render anything until initialized to prevent white flash
  if (!isInitialized) {
    return <div style={containerStyle}></div>;
  }

  // Show loading state only for initial load
  if (isLoading && !chartData) {
    return (
      <div style={{
        ...containerStyle,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ 
            width: '40px', 
            height: '40px', 
            border: '4px solid #333333',
            borderTop: '4px solid #007bff',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 10px'
          }}></div>
          <p style={{ color: '#ffffff', margin: 0 }}>Loading chart...</p>
        </div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  // Show validation errors if any
  if (dataValidationErrors.length > 0) {
    return (
      <div style={{ ...containerStyle, padding: '20px', color: '#ffffff' }}>
        <div style={{ color: '#ff6b6b', marginBottom: '10px' }}>
          <h4>Data Validation Errors:</h4>
          <ul>
            {dataValidationErrors.map((error, i) => <li key={i}>{error}</li>)}
          </ul>
        </div>
      </div>
    );
  }

  // Show message if no valid chart data
  if (!chartData) {
    return (
      <div style={{
        ...containerStyle,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <p style={{ color: '#ffffff', margin: 0 }}>No valid data to display</p>
      </div>
    );
  }

  const safeColors =
    graph_det?.colors?.length >= 3
      ? graph_det.colors
      : ['#4ade80', '#facc15', '#ef4444']; // Fallback colors: green, yellow, red

  const getColor = (datum) => {
    const value = Math.abs(datum.data.y);
    if (value <= graph_det.min_threshold) {
      return safeColors[0];
    }
    if (value >= graph_det.max_threshold) {
      return safeColors[2];
    }
    return safeColors[1];
  };

  return (
    <div style={containerStyle}>
      <ResponsiveBar
        data={chartData}
        keys={['y']}
        indexBy='x'
        padding={0.4}
        borderRadius={4}
        enableGridX={false}
        enableGridY={true}
        margin={{ top: 30, right: 60, bottom: 80, left: 60 }}
        colors={getColor}
        borderWidth={1}
        borderColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
        valueScale={{ type: "linear" }}
        axisTop={null}
        axisRight={null}
        xScale={{
          type: 'time',
          format: '%Y-%m-%dT%H:%M:%S.%LZ',
          useUTC: false,
        }}
        xFormat="time:%Y-%m-%d %H:%M:%S"
        yScale={{
          type: 'linear',
          min: 'auto',
          max: 'auto',
          stacked: false,
          reverse: false  // Ensure bars are not inverted
        }}
        axisBottom={{
          format: value => new Date(value).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          legend: 'Time',
          legendOffset: 45,
          legendPosition: 'middle',
          tickRotation: -45,
          tickSize: 5,
          tickPadding: 5,
        }}
        axisLeft={{
          orient: 'left',
          tickSize: 5,
          tickPadding: 5,
          tickRotation: 0,
          legend: 'Value',
          legendOffset: -40,
          legendPosition: 'middle',  
        }}
        labelSkipWidth={12}
        labelSkipHeight={12}
        labelTextColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
        animate={true}
        motionStiffness={90}
        motionDamping={15}
        tooltip={({ value, color, indexValue }) => (
            <div
                style={{
                    padding: '5px 10px',
                    background: 'rgba(0, 0, 0, 0.85)',
                    color: 'white',
                    borderRadius: '3px',
                }}
            >
                <strong>Time: {new Date(indexValue).toLocaleString()}</strong>
                <br />
                <strong style={{ color: color }}>Value: {value}</strong>
            </div>
        )}
        theme={{
          axis: {
              domain: {
                  line: {
                      stroke: 'white',
                      strokeWidth: 1
                  }
              },
              ticks: {
                  line: {
                      stroke: 'white',
                      strokeWidth: 1
                  },
                  text: {
                      fill: 'white'
                  }
              },
              legend: {
                  text: {
                      fill: 'white'
                  }
              }
          },
          grid: {
              line: {
                  stroke: 'rgba(255, 255, 255, 0.2)'
              }
          },
          tooltip: {
            container: {
              background: 'rgba(0, 0, 0, 0.85)',
              color: 'white',
            },
          },
        }}
      />
    </div>
  );
}

export default BarChart;
